import { Badge } from "@/components/ui/badge";
import { RuleChip } from "@/components/ui/rule-chip";
import { usePersistentState } from "@/hooks/use-persistent-state";
import {
	formatMessage,
	getRuleDisplayLabel,
	getRuleDisplayName,
	reconciliationResultMessages,
	reconciliationRuleMessages,
} from "@/services/messages";
import { AlertTriangle, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import { useMemo } from "react";
import { DiscrepancyAggregatedTable } from "./DiscrepancyAggregatedTable";
import { DiscrepancyGrandTotalCard } from "./DiscrepancyGrandTotalCard";
import { DiscrepancyMatrixCard } from "./DiscrepancyMatrixCard";
import { DiscrepancyRelatedSection } from "./DiscrepancyRelatedSection";
import { DiscrepancySigmaStrip } from "./DiscrepancySigmaStrip";
import { SeverityBadge } from "./SeverityBadge";
import { cardClassName, railColor } from "./lib/card-style";
import { extractCodeFromGroupKey } from "./lib/group-key";
import { levelLabel } from "./lib/level-label";
import { pickCardVariant } from "./lib/pickCardVariant";
import type { DiscrepancyCardViewModel } from "./lib/view-model";

/**
 * Карточка расхождения — главный примитив отчёта (фича 024 ①, замена three-column
 * layout фичи 023). Severity-rail слева 4px, header с rule-chip(s) + meta + title +
 * subtitle, σ-strip с агрегатами, embedded sortable таблица источников.
 *
 * Этот компонент рендерит **aggregated** layout. Для multi-rule с numeric
 * сравнениями родитель должен выбрать `DiscrepancyMatrixCard` через
 * `pickCardVariant` (Phase 3).
 *
 * Открытие/закрытие таблицы persisted в localStorage по `runId+discrepancyId`.
 */
export interface DiscrepancyCardProps {
	viewModel: DiscrepancyCardViewModel;
	/** ID прогона — нужен для persist key. */
	runId?: string;
	/** Если true — карточка визуально подсвечена (drill-down target, US2). */
	highlighted?: boolean;
	/** Колбэк клика по drill-down ссылке (FR-013c). */
	onRelatedClick?: (targetId: string) => void;
}

/**
 * Top-level dispatcher: выбирает grand-total / matrix / aggregated layout
 * по правилам `pickCardVariant` (фича 024 — Phase 3.2). Подробности в
 * `lib/pick-card-variant.ts`. Sprint 6 / C1.3: устаревшая ссылка на
 * `hasNumericComparisons` удалена — vm-поле выкинуто как мёртвое.
 */
export function DiscrepancyCard(props: DiscrepancyCardProps) {
	const variant = pickCardVariant(props.viewModel);
	if (variant === "grand-total") {
		return <DiscrepancyGrandTotalCard {...props} />;
	}
	if (variant === "matrix") {
		return <DiscrepancyMatrixCard {...props} />;
	}
	return <DiscrepancyAggregatedCard {...props} />;
}

/**
 * tableKind выбирается по «нагруженной» стороне:
 * - RC-P1 (лишнее в акте) → actSources непустой, woSources пустой → "act"
 * - RC-P10 (бескодовая в наряде) → woSources непустой, actSources пустой → "work-order"
 * - Single-rule с обеих сторон (RC-P5/P7 в одиночку) → выбирается сторона
 *   с большим числом источников. Меньшая сторона не показывается в этом
 *   варианте (используй MatrixCard для multi-rule сравнений).
 */
function DiscrepancyAggregatedCard({
	viewModel,
	runId,
	highlighted = false,
	onRelatedClick,
}: DiscrepancyCardProps) {
	const messages = reconciliationResultMessages.redesign;
	// Sprint 6 / C1.4: при `runId === undefined` пустой ключ — usePersistentState
	// работает как обычный useState без записи в localStorage и без засорения
	// «no-run»-ключа (паттерн из usePinnedDiscrepancies).
	const persistKey = runId ? `recon:card-open:${runId}:${viewModel.id}` : "";
	const [open, setOpen] = usePersistentState<boolean>(persistKey, true);

	const code = extractCodeFromGroupKey(viewModel.groupKey);

	// Для aggregated-варианта берём первое правило для названия + subtitle.
	const firstRule = viewModel.violations[0];
	const ruleId = firstRule?.ruleId ?? "";
	const ruleName = ruleId ? getRuleDisplayName(ruleId) : "";
	const ruleLabel = ruleId ? getRuleDisplayLabel(ruleId) : "";

	// workName — из первого источника (act предпочтительнее, иначе wo).
	// Для агрегированных карточек RC-P9 / RC-P10 (groupKey `position|act-uncoded`
	// или `position|wo-uncoded`) workName первой строки бессмыслен — там много
	// разных работ, ни одна не репрезентативна. В этом случае используем только
	// ruleName.
	const isUncodedAggregate =
		viewModel.groupKey === "position|act-uncoded" ||
		viewModel.groupKey === "position|wo-uncoded";
	const sampleWorkName = isUncodedAggregate
		? ""
		: (viewModel.actSources[0]?.workName ?? viewModel.workOrderSources[0]?.workName ?? "");

	// Title: «Лишняя позиция в акте: «{workName}»» либо просто «Расхождение».
	const title = ruleName
		? sampleWorkName
			? `${ruleName}: «${sampleWorkName}»`
			: ruleName
		: viewModel.summary;

	// Какая сторона расхождения «нагружена» источниками — определяет какую таблицу
	// показывать в aggregated-варианте.
	const tableKind: "act" | "work-order" =
		viewModel.actSources.length >= viewModel.workOrderSources.length ? "act" : "work-order";
	const sources = tableKind === "act" ? viewModel.actSources : viewModel.workOrderSources;

	// Subtitle: per-rule template (rule.subtitle_template). Для каждого правила своя
	// семантика — например, RC-P7 показывает «стоимость расходится: N нарядов ↔ M актов»,
	// а RC-P1 — «N строк в актах нет ни в одном наряде». Generic fallback на старый
	// шаблон используется только если правило не имеет subtitle_template.
	const subtitle = useMemo(() => {
		if (sources.length === 0) return "";
		const woCount = viewModel.workOrderSources.length;
		const actCount = viewModel.actSources.length;
		const vars = { count: sources.length, woCount, actCount, code };
		const ruleTpl = ruleId
			? reconciliationRuleMessages[ruleId.toLowerCase()]?.subtitle_template
			: undefined;
		if (ruleTpl) {
			return formatMessage(ruleTpl, vars);
		}
		return formatMessage(messages.aggregated_subtitle_template, vars);
	}, [
		sources.length,
		viewModel.workOrderSources.length,
		viewModel.actSources.length,
		code,
		ruleId,
		messages.aggregated_subtitle_template,
	]);

	// Σ-strip aggregates.
	// Стоимость суммируем как сумму Math.round — чтобы visually совпадала
	// с сложением отображаемых целых рублей в таблице (см. fmtRub).
	const sigma = useMemo(() => {
		const totalQty = sources.reduce((acc, s) => acc + s.quantity, 0);
		const totalCost = sources.reduce((acc, s) => acc + Math.round(getCost(s, tableKind)), 0);
		const filesCount = new Set(sources.map((s) => s.fileId)).size;
		const unit = sources[0]?.unit ?? null;
		return {
			sourcesCount: sources.length,
			filesCount,
			totalQty,
			totalCost,
			unit,
		};
	}, [sources, tableKind]);

	return (
		<article
			id={`discrepancy-${viewModel.id}`}
			aria-label={viewModel.summary}
			className={cardClassName(viewModel.severity, highlighted)}
		>
			<div className="flex">
				<div className={`w-1 ${railColor(viewModel.severity)}`} aria-hidden="true" />
				<div className="min-w-0 flex-1">
					{/* Header */}
					<header className="flex items-start gap-3 px-4 pt-3 pb-2">
						{viewModel.severity === "error" ? (
							<XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
						) : (
							<AlertTriangle
								className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500"
								aria-hidden="true"
							/>
						)}
						<div className="min-w-0 flex-1">
							<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
								<RuleChip>{ruleLabel}</RuleChip>
								{code ? (
									<span className="font-mono text-[11px] text-slate-500">код {code}</span>
								) : null}
								<span className="text-slate-300">·</span>
								<span className="text-[11px] text-slate-500">{levelLabel(viewModel.level)}</span>
								<span className="ml-auto">
									<SeverityBadge severity={viewModel.severity} />
								</span>
							</div>
							<h3 className="mt-1 text-[14px] font-semibold leading-snug text-slate-900 text-pretty">
								{title}
							</h3>
							{subtitle ? <p className="mt-0.5 text-[12px] text-slate-500">{subtitle}</p> : null}
						</div>
					</header>

					{/* Σ-strip */}
					<DiscrepancySigmaStrip
						sourcesCount={sigma.sourcesCount}
						filesCount={sigma.filesCount}
						totalQuantity={sigma.totalQty}
						unit={sigma.unit}
						totalCost={sigma.totalCost}
						periodDates={[]}
					/>

					{/* Toggle row + embedded table */}
					<div className="border-t border-slate-100 bg-slate-50/40">
						<button
							type="button"
							onClick={() => setOpen((o) => !o)}
							aria-expanded={open}
							className="flex w-full items-center justify-between px-4 py-2 text-[12px] hover:bg-slate-50"
						>
							<span className="inline-flex items-center gap-2 font-medium text-slate-700">
								{open ? (
									<ChevronDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
								) : (
									<ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
								)}
								{messages.aggregated_toggle_label}
								<span className="rounded bg-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
									{sources.length}
								</span>
							</span>
							<span className="text-[11px] text-slate-400">
								{messages.aggregated_sort_hint} ·{" "}
								<kbd className="rounded bg-slate-100 px-1 font-mono text-[10px] text-slate-500">
									{messages.aggregated_kbd_sort}
								</kbd>
							</span>
						</button>
						{open ? (
							tableKind === "act" ? (
								<DiscrepancyAggregatedTable
									sources={viewModel.actSources}
									kind="act"
									unit={sigma.unit}
								/>
							) : (
								<DiscrepancyAggregatedTable
									sources={viewModel.workOrderSources}
									kind="work-order"
									unit={sigma.unit}
								/>
							)
						) : null}
					</div>

					{/* Drill-down related */}
					{viewModel.hasRelated && onRelatedClick ? (
						<div className="px-4 pb-3">
							<DiscrepancyRelatedSection
								relatedIds={viewModel.relatedDiscrepancyIds}
								onRelatedClick={onRelatedClick}
							/>
						</div>
					) : null}
				</div>
			</div>
		</article>
	);
}

function getCost(
	s:
		| DiscrepancyCardViewModel["actSources"][number]
		| DiscrepancyCardViewModel["workOrderSources"][number],
	kind: "act" | "work-order",
): number {
	if (kind === "act") {
		return (s as DiscrepancyCardViewModel["actSources"][number]).salaryTotal;
	}
	return (s as DiscrepancyCardViewModel["workOrderSources"][number]).amount;
}

// Sprint 6 / C11.2: SeverityBadge вынесен в собственный модуль (импорт сверху).
